import type { ShopperCustomersTypes } from 'commerce-sdk-isomorphic'

export default function useWishlist() {
  /** Product ids joined by "-", written for guests before the SFCC list was the only record. */
  const legacyGuestWishlistCookie = () =>
    useCookie<string | number | null>(`guestWishlistCookie_${useShopper().config.siteId}`, { path: '/' })

  function flattenProductListItem(
    wishlistId: string,
    product: ShopperCustomersTypes.CustomerProductListItem,
  ): FlattenedWishlistItem {
    const { id: listItemId = '', ...filteredProduct } = product
    return {
      listId: wishlistId,
      listItemId,
      ...filteredProduct,
    }
  }

  function flattenProductLists(wishlists: Array<ShopperCustomersTypes.CustomerProductList>) {
    let productLists: any[] = []
    wishlists?.map((wishlist: ShopperCustomersTypes.CustomerProductList) => {
      if (wishlist.customerProductListItems) {
        productLists = productLists.concat(
          wishlist.customerProductListItems.map((product: ShopperCustomersTypes.CustomerProductListItem) => {
            return flattenProductListItem(wishlist.id ?? '', product)
          }),
        )
      }
    })

    return productLists
  }

  async function getWishlist() {
    try {
      const customerId = useUserStore().user?.customerId ?? useUserStore().guestUser?.customerId
      if (!customerId) {
        return
      }

      const { getAuthHeaders, shopperCustomers, config } = useShopper()

      const response = await shopperCustomers.getCustomerProductLists({
        headers: await getAuthHeaders(),
        parameters: {
          organizationId: config.organizationId,
          customerId,
          siteId: config.siteId,
        },
      })
      // Always overwritten, so signing in to an account with no list doesn't keep the Guest's items.
      const lists = response.total === 0 ? [] : response.data
      useWishlistStore().wishlist = flattenProductLists(lists)
      return lists
    } catch (e) {
      return await salesforceErrorHandler(e)
    }
  }

  async function createWishlist() {
    try {
      const customerId = useUserStore().user?.customerId ?? useUserStore().guestUser?.customerId
      if (!customerId) {
        return
      }

      const { getAuthHeaders, shopperCustomers, config } = useShopper()

      const response = await shopperCustomers.createCustomerProductList({
        headers: await getAuthHeaders(),
        parameters: {
          organizationId: config.organizationId,
          customerId,
          siteId: config.siteId,
        },
        body: {
          type: 'wish_list',
        },
      })

      return response.id
    } catch (e) {
      return await salesforceErrorHandler(e)
    }
  }

  async function addItemToWishlist(listId: string, productId: string) {
    try {
      const customerId = useUserStore().user?.customerId ?? useUserStore().guestUser?.customerId
      if (!customerId || !listId) {
        return
      }

      const { getAuthHeaders, shopperCustomers, config } = useShopper()

      const response = await shopperCustomers.createCustomerProductListItem({
        headers: await getAuthHeaders(),
        parameters: {
          organizationId: config.organizationId,
          customerId,
          siteId: config.siteId,
          listId,
        },
        body: {
          productId,
          public: false,
          priority: 1,
          quantity: 1,
          type: 'product',
        },
      })

      const flattennedProductListItem = flattenProductListItem(listId, response)
      useWishlistStore().addItem(flattennedProductListItem)
      return flattennedProductListItem
    } catch (e) {
      return await salesforceErrorHandler(e)
    }
  }

  async function removeItemFromWishlist(listId: string, itemId: string) {
    try {
      let response
      const customerId = useUserStore().user?.customerId ?? useUserStore().guestUser?.customerId
      if (customerId && listId) {
        const { getAuthHeaders, shopperCustomers, config } = useShopper()

        response = await shopperCustomers.deleteCustomerProductListItem({
          headers: await getAuthHeaders(),
          parameters: {
            organizationId: config.organizationId,
            customerId,
            siteId: config.siteId,
            listId,
            itemId,
          },
        })
      }

      useWishlistStore().removeItem(itemId)
      return response
    } catch (e) {
      return await salesforceErrorHandler(e)
    }
  }

  return {
    legacyGuestWishlistCookie,
    getWishlist,
    createWishlist,
    addItemToWishlist,
    removeItemFromWishlist,
  }
}
